import {
  Badge,
  Button,
  Center,
  Group,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertTriangle, IconCalendarOff, IconChevronRight } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import type { RefreshInterval } from '../api/queries';
import { useCategoryTitle, usePlannedOperations } from '../api/queries';
import type { PlannedOperation } from '../api/types';
import { formatDate, formatMoney } from '../lib/format';
import { PlannedOperationDetails } from './PlannedOperationDetails';

export const PLANNED_OPERATIONS_LIMIT = 50;

export function PlannedOperationsPanel({
  refreshInterval,
  onCountChange,
}: {
  refreshInterval: RefreshInterval;
  onCountChange?: (count: number | null) => void;
}) {
  const { data, isPending, error, refetch } = usePlannedOperations(
    PLANNED_OPERATIONS_LIMIT,
    refreshInterval,
  );
  const [selectedOperationId, setSelectedOperationId] = useState<string | null>(null);
  const operations = data?.plannedOperations ?? [];

  useEffect(() => {
    onCountChange?.(error !== null || isPending ? null : operations.length);
  }, [error, isPending, onCountChange, operations.length]);

  return (
    <>
      <Stack gap="md">
        {error ? (
          <Center py={24}>
            <Stack gap="xs" align="center">
              <ThemeIcon variant="light" color="red" size="lg" radius="md">
                <IconAlertTriangle size={18} stroke={1.5} />
              </ThemeIcon>
              <Text size="sm" fw={600}>
                Не удалось загрузить планы
              </Text>
              <Button
                size="xs"
                variant="default"
                onClick={() => {
                  void refetch();
                }}
              >
                Повторить
              </Button>
            </Stack>
          </Center>
        ) : isPending ? (
          <Stack gap="sm">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} height={48} radius="sm" />
            ))}
          </Stack>
        ) : operations.length === 0 ? (
          <Center py={28}>
            <Stack gap="xs" align="center">
              <IconCalendarOff size={26} stroke={1.5} opacity={0.5} />
              <Text size="sm" c="dimmed" ta="center">
                Активных разовых планов нет
              </Text>
            </Stack>
          </Center>
        ) : (
          <Stack gap={0}>
            {operations.map((operation) => (
              <PlannedOperationRow
                key={operation.id}
                operation={operation}
                onOpen={() => setSelectedOperationId(operation.id)}
              />
            ))}
            {data?.hasMore && (
              <Text size="xs" c="dimmed" ta="center" mt="sm">
                Показаны ближайшие {PLANNED_OPERATIONS_LIMIT}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
      <PlannedOperationDetails
        plannedOperationId={selectedOperationId}
        onClose={() => setSelectedOperationId(null)}
      />
    </>
  );
}

function PlannedOperationRow({
  operation,
  onOpen,
}: {
  operation: PlannedOperation;
  onOpen: () => void;
}) {
  const categoryTitle = useCategoryTitle();
  const deposit = operation.kind === 'deposit';

  return (
    <UnstyledButton
      className="planned-operation-row"
      onClick={onOpen}
      aria-label={`Открыть детали «${operation.title}»`}
    >
      <Group justify="space-between" wrap="nowrap" py="xs" px={4}>
        <Stack gap={1} style={{ minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" fw={500} lineClamp={1}>
              {operation.title}
            </Text>
            {operation.status === 'tentative' && (
              <Badge size="xs" color="gray" variant="light">
                предварительно
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {formatDate(operation.due_on)}
            {operation.category ? ` · ${categoryTitle(operation.category)}` : ''}
          </Text>
        </Stack>
        <Group gap="xs" wrap="nowrap" style={{ whiteSpace: 'nowrap' }}>
          <Text
            size="sm"
            fw={600}
            c={deposit ? 'teal' : 'inherit'}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {deposit ? '+' : '−'}
            {formatMoney(operation.amount)}
          </Text>
          <IconChevronRight size={17} stroke={1.5} opacity={0.55} />
        </Group>
      </Group>
    </UnstyledButton>
  );
}
